import { useCallback, useEffect, useRef, useState } from 'react';
import React from 'react';
import { shallow } from 'zustand/shallow';

import { useSessionStore } from 'session/useSessionStore';
import { useSaveSLD } from '../api/useSLDController';
import { IGridXyzBasicInfo } from '../models/interfaces/IGridXyzBasicInfo';
import { use3DGridStore } from '../stores/use3DGridStore';
import { use3DViewerStore } from '../stores/use3DViewerStore';
import * as ThreeJsGridUtils from '../threejs/utils/GeopostGridUtils';
import { getGridGroupName, getSLDAlpha, getSLDMainGrayScale } from '../threejs/utils/GeopostGridUtils';
import { useWMS } from '../api/useMapProxy';
import { WmsRequest } from '../models/types/WmsRequest';
import { GeopostThreeJsConstants } from '../threejs/utils/GeopostThreeJsConstants';
import { use3DSceneStore } from '../stores/use3DSceneStore';
import { GeopostGridGroup } from '../threejs/group/GeopostGridGroup';
import { IGridXyz } from '../models/interfaces/IGridXyz';
import { GeopostSceneContainer } from './GeopostSceneContainer';

export const GridXyz3D = ({grid, mainFeatureSrid, opacity, visible, onLoadEnd, onLoadStart, scale, projectionHeight = null} : GridXyz3DProps) => {

    const { mutate: saveSLD } = useSaveSLD();

    const sldHost = useSessionStore(state => state.tenantConfiguration?.endpoints.sld) ?? '';
    const geopostScene = use3DSceneStore(state => state.scene);;

    (ThreeJsGridUtils.checkGridVisibility(grid.Token, geopostScene!));

    const {
        featureCentroidX,
        featureCentroidY,
        countTimeDepth,
        heightPixelFactor
    } = use3DViewerStore(state => ({
        featureCentroidX: state.featureCentroidX,
        featureCentroidY: state.featureCentroidY,
        countTimeDepth: state.countTimeDepth,
        heightPixelFactor: state.heightPixelFactor
    }), shallow);

    const lastHeightPixelFactorRef = useRef<number>(heightPixelFactor);

    useEffect(() => {
        if (ThreeJsGridUtils.checkIfGridHasMesh(grid.Token, geopostScene!)) {
            ThreeJsGridUtils.changeGridMeshVisbility(grid.Token, geopostScene!, visible);
        }
        else if (visible) {
            buildGridOnScene(grid);
        }
    }, [visible]);

    const [ wmsColorRequest, setWmsColorRequest ] = useState<WmsRequest>();
    const [ wmsDisplacementRequest, setWmsDisplacementRequest ] = useState<WmsRequest>();

    const { data: wmsColor } = useWMS(wmsColorRequest);
    const { data: wmsDisplacement } = useWMS(wmsDisplacementRequest);

    const addGrid = async () => {
        if (!!wmsColor && !!wmsDisplacement) {
            const group = await ThreeJsGridUtils.getGridGroup(grid, wmsDisplacement, wmsColor, countTimeDepth, heightPixelFactor, featureCentroidX, featureCentroidY);
            group.userData.Token = grid.Token;
            if (projectionHeight !== null && !!group) {
                updateProjectionHeight(projectionHeight, group);
            }
            geopostScene!.add(group);
            onLoadEnd();
        }
    };

    const updateProjectionHeight = useCallback((height: number, gridGroup: THREE.Object3D) => {
        if (!!gridGroup) {
            gridGroup?.userData.coloredLayerMesh.position.setY(height / gridGroup.scale.y);
            gridGroup?.userData.shadedLayerMesh.position.setY(height / gridGroup.scale.y);
        }
    }, []);

    useEffect(() => {
        addGrid();
    }, [wmsColor, wmsDisplacement]);

    useEffect(() => {
        if (heightPixelFactor !== 0 && heightPixelFactor !== lastHeightPixelFactorRef.current) {
            if (visible) {
                ThreeJsGridUtils.removeGrid(grid.Token, geopostScene!);
                onLoadStart();
                addGrid();
            }
            lastHeightPixelFactorRef.current = heightPixelFactor;
        }
    }, [heightPixelFactor]);

    useEffect(() => {
        ThreeJsGridUtils.changeGridOpacity(grid.Token, geopostScene!, opacity);
    }, [opacity]);

    useEffect(() => {
        if (projectionHeight !== null) {
            const gridGroup = geopostScene?.getObjectByName(getGridGroupName(grid.Token));
            if (!!gridGroup) {
                updateProjectionHeight(projectionHeight, gridGroup);
            }
        }
    }, [projectionHeight]);

    const buildGridOnScene = (grid: IGridXyz) => {
        onLoadStart();
        const mainGrayScaleSLD = getSLDMainGrayScale(grid.Min, grid.Max, grid.Workspace, grid.LayerName);
        const alphaSLD = getSLDAlpha(grid.Min, grid.Max, grid.Workspace, grid.LayerName, grid.SimplifiedGeomWkt);
        saveSLD(mainGrayScaleSLD, {
            onSuccess: (mainGrayScaleSLDToken) => {
                saveSLD(alphaSLD, {
                    onSuccess: (alphaSLDToken) => {
                        const extent = [grid.MinX, grid.MinY, grid.MaxX, grid.MaxY];
                        setWmsColorRequest({
                            workspace: grid.Workspace,
                            layerName: grid.LayerName,
                            hostServer: grid.HostServer,
                            sldHost: sldHost,
                            extent: extent,
                            backgroundColor: '000000',
                            imageFormat: 'image/jpeg',
                            width: GeopostThreeJsConstants.pictureTextureWidth,
                            height: GeopostThreeJsConstants.pictureTextureHeight,
                            sldToken: alphaSLDToken,
                            srid: mainFeatureSrid
                        });
                        setWmsDisplacementRequest({
                            workspace: grid.Workspace,
                            layerName: grid.LayerName,
                            hostServer: grid.HostServer,
                            sldHost: sldHost,
                            extent: extent,
                            width: GeopostThreeJsConstants.pictureTextureWidth,
                            height: GeopostThreeJsConstants.pictureTextureHeight,
                            sldToken: mainGrayScaleSLDToken,
                            srid: mainFeatureSrid,
                            backgroundColor: undefined,
                            imageFormat: 'image/png'
                        });
                    }
                });
            }
        });
    };

    useEffect(() => {
        const gridObj = geopostScene?.getObjectByName(getGridGroupName(grid.Token));
        const gridObjScale = gridObj?.scale.y;
        if (!!gridObjScale && !!scale && gridObjScale !== scale) {
            gridObj.scale.setY(scale);
            if (!!projectionHeight) {
                updateProjectionHeight(projectionHeight, gridObj);
            }
        }
    }, [scale]);

    return (
        <React.Fragment></React.Fragment>
    );
};

export type GridXyz3DProps = {
    grid: IGridXyz,
    mainFeatureSrid: number,
    opacity: number,
    visible: boolean,
    onLoadStart: () => void,
    onLoadEnd: () => void,
    scale: number,
    projectionHeight?: number|null
};
